import { z } from 'zod';

/**
 * Response shapes of the backend's unified-alerts endpoints (`/public/alerts/*`).
 *
 * Client-safe on purpose (no `server-only`): the Next proxy routes parse the
 * backend payload with these schemas before forwarding it, and the client
 * hooks infer their types from the same definitions, so the two cannot drift.
 *
 * Vocabularies (cadence, channel, status, feed type) are typed as plain
 * strings here — the backend owns them, and a proxy that restated them as
 * enums is exactly what silently 422'd valid watch configs before (see
 * `app/api/lists/[listId]/watch/route.ts`). The UI narrows them for display
 * and falls back to the raw value for anything it does not know yet.
 */

/** Cadences the UI offers. The backend is the authority; this is display only. */
export const ALERT_CADENCES = ['immediately', 'daily', 'weekly', 'monthly'] as const;
export type AlertCadence = (typeof ALERT_CADENCES)[number];

export type AlertSubjectType = 'company' | 'list' | 'feed';
export type FeedType = 'insolvencies' | 'new_companies' | 'tenders' | 'saved_search';

/** The digest_frequency the legacy watch columns still expect for a cadence. */
export function digestFrequencyFor(cadence: string): 'daily' | 'weekly' {
  return cadence === 'immediately' || cadence === 'daily' ? 'daily' : 'weekly';
}

/**
 * One row of `GET /public/alerts/subscriptions`
 * (`backend/alerts/subscriptions.py::listing`). Create and PATCH return a
 * subset of these fields, parsed with `.partial()`.
 */
export const alertSubscriptionSchema = z.object({
  public_id: z.string(),
  /** Internal row id. Edits are quoted by `public_id` instead. */
  id: z.number().int().nullish(),
  subject_type: z.string(),
  feed_type: z.string().nullish(),
  name: z.string().nullish(),
  eu_id: z.string().nullish(),
  company_name: z.string().nullish(),
  list_id: z.number().int().nullish(),
  list_name: z.string().nullish(),
  saved_search_id: z.number().int().nullish(),
  saved_search_name: z.string().nullish(),
  criteria: z.array(z.string()).nullish(),
  filters: z.record(z.string(), z.unknown()).nullish(),
  cadence: z.string().nullish(),
  channel: z.string().nullish(),
  recipient_emails: z.array(z.string()).nullish(),
  paused_at: z.string().nullish(),
  paused_reason: z.string().nullish(),
  billing_exempt: z.boolean().nullish(),
  daily_cost: z.number().nullish(),
  status: z.string().nullish(),
  created_at: z.string().nullish(),
});
export type AlertSubscription = z.infer<typeof alertSubscriptionSchema>;

export const alertSubscriptionsResponseSchema = z.object({
  items: z.array(alertSubscriptionSchema),
  total_daily_cost: z.number().nullish(),
  balance: z.number().nullish(),
});
export type AlertSubscriptionsResponse = z.infer<
  typeof alertSubscriptionsResponseSchema
>;

export const alertCostSchema = z.object({
  total: z.number(),
  companies: z.number().nullish(),
  per_subscription: z
    .array(
      z.object({
        id: z.number().int(),
        public_id: z.string().nullish(),
        daily_cost: z.number(),
        exempt: z.boolean().nullish(),
      }),
    )
    .nullish(),
  balance: z.number().nullish(),
  days_left: z.number().nullish(),
});
export type AlertCost = z.infer<typeof alertCostSchema>;

export const alertQuoteSchema = z.object({
  increment: z.number(),
  total_after: z.number().nullish(),
});
export type AlertQuote = z.infer<typeof alertQuoteSchema>;

export const alertPricingSchema = z
  .object({ billing_mode: z.string().nullish() })
  .catchall(z.unknown());
export type AlertPricing = z.infer<typeof alertPricingSchema>;

export const alertSettingsSchema = z.object({
  alert_emails: z.boolean().nullish(),
  credit_emails: z.boolean().nullish(),
  locale: z.string().nullish(),
  email: z.string().nullish(),
  email_verified: z.boolean().nullish(),
  opted_out_at: z.string().nullish(),
});
export type AlertSettings = z.infer<typeof alertSettingsSchema>;

/** The 402 body the backend sends when an alert cannot be prepaid. */
export const insufficientCreditsSchema = z.object({
  error: z.literal('insufficient_credits'),
  needed: z.number().nullish(),
  balance: z.number().nullish(),
});

/**
 * What `/api/account/notification-settings` returns — the backend's alert
 * settings merged with the credit-mail preference that still lives in the
 * auth DB. `null` means the owner could not be reached; the UI disables that
 * control rather than guessing.
 */
export interface NotificationSettingsData {
  alertEmails: boolean | null;
  creditEmails: boolean;
  locale: 'de' | 'en' | null;
  optedOut: boolean;
}
